import { Drawer, DrawerProps, PaperProps } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';

/**
 * DrawerContainer wraps MUI Drawer and applies a fixed layout (header / body / footer grid)
 * to the Paper (the sliding panel). You can customize the Paper in three ways:
 *
 * 1. fullHeight — Boolean. When true, the drawer fills the viewport height, has no
 *    top border radius, and uses a grey.50 paper background. Use for full-screen flows
 *    (e.g. escalate alert). Keep footers white with `sx={{ bgcolor: 'common.white' }}`.
 *
 * 2. paperSx — Top-level sx overrides for the Paper. Merged *after* any sx from slotProps.paper,
 *    so paperSx wins when both set the same property. Use for one-off style overrides:
 *    <DrawerContainer paperSx={{ pt: 2 }} />
 *
 * 3. slotProps.paper — Full Paper slot props (sx, className, elevation, etc.). Use when you
 *    need to pass multiple Paper props or when using DrawerContainer in a wrapper that
 *    forwards slotProps. Any sx here is merged before paperSx (paperSx wins on conflict).
 */
export interface DrawerContainerProps extends DrawerProps {
    fullHeight?: boolean;
    /** Sx overrides applied to the Paper slot (merged after default container styles) */
    paperSx?: SxProps<Theme>;
    slotProps?: DrawerProps['slotProps'];
}

function DrawerContainer(props: DrawerContainerProps) {
    const { fullHeight = false, paperSx, slotProps, ...rest } = props;
    const theme = useTheme();

    // Base layout styles for the Paper: grid with header/body/footer areas used by
    // DrawerTitle, DrawerBody, DrawerFooter. Must live in sx so MUI applies them as CSS.
    const basePaperSx: SxProps<Theme> = {
        display: 'grid',
        boxSizing: 'border-box',
        maxHeight: '100dvh',
        borderRadius: `${theme?.spacing(2.5)} ${theme?.spacing(2.5)} 0 0`,
        overflow: 'hidden',
        gridTemplateAreas: "'header' 'body' 'footer'",
        gridTemplateRows: 'max-content 1fr max-content',
        ...(fullHeight
            ? {
                  height: '100dvh',
                  borderRadius: 0,
                  backgroundColor: 'grey.50',
              }
            : {}),
    };

    // slotProps.paper can be an object OR a function (MUI allows both). We only read from it
    // when it's an object so we don't call a function or spread undefined.
    const existingPaper =
        slotProps?.paper && typeof slotProps.paper === 'object' ? slotProps.paper : {};

    // Pull out the caller's sx from slotProps.paper if present (TypeScript doesn't guarantee
    // 'sx' on the slot type, so we check at runtime).
    const existingSx = 'sx' in existingPaper ? (existingPaper as PaperProps).sx : undefined;

    // Merge order for sx: [ basePaperSx, slotProps.paper.sx, paperSx ]. Later entries override.
    // Flatten so we never pass nested arrays (existingSx/paperSx may already be arrays).
    const mergedPaperSx = [basePaperSx, existingSx, paperSx]
        .filter(Boolean)
        .flat() as SxProps<Theme>[];

    return (
        <Drawer
            data-testid="drawer-container"
            slotProps={{
                ...slotProps,
                paper: {
                    // Caller's other Paper props (className, elevation, ref, etc.) — not sx
                    ...existingPaper,
                    // All layout/visual styles go through sx so they're applied as CSS, not DOM attrs
                    sx: mergedPaperSx as SxProps<Theme>,
                },
            }}
            // Spread the rest of the props to the Drawer (anchor, open, onClose, etc.)
            // Any sx here affects the Drawer's root element, not the Paper.
            {...rest}
        />
    );
}

export default DrawerContainer;
